import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from './assets/OS logo inverted.png';
import mailIcon from './assets/mail-icon.png';
import instagramIcon from './assets/instagram-icon.png';
import './NewsletterPage.css';

// Reads MailerLite configuration from environment variables so secrets stay out of source control.
const MAILERLITE_API_BASE = 'https://connect.mailerlite.com/api';
const MAILERLITE_API_URL = `${MAILERLITE_API_BASE}/subscribers`;
const MAILERLITE_TOKEN = process.env.REACT_APP_MAILERLITE_TOKEN;
const MAILERLITE_GROUP_ID = process.env.REACT_APP_MAILERLITE_GROUP_ID?.trim();

function NewsletterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [status, setStatus] = useState('idle');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Ensures existing subscribers are added to the desired group.
  const ensureSubscriberInGroup = async (email) => {
    if (!MAILERLITE_GROUP_ID) {
      return;
    }

    const lookupResponse = await fetch(
      `${MAILERLITE_API_URL}?filter[email]=${encodeURIComponent(email)}`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${MAILERLITE_TOKEN}`,
        },
      }
    );

    if (!lookupResponse.ok) {
      throw new Error('Unable to lookup existing subscriber.');
    }

    const lookupResult = await lookupResponse.json();
    const subscriberId = lookupResult?.data?.[0]?.id;

    if (!subscriberId) {
      throw new Error('Subscriber record was not returned by MailerLite.');
    }

    const groupResponse = await fetch(`${MAILERLITE_API_BASE}/subscribers/${subscriberId}/groups`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${MAILERLITE_TOKEN}`,
      },
      body: JSON.stringify({ groups: [MAILERLITE_GROUP_ID] }),
    });

    if (!groupResponse.ok) {
      throw new Error('Failed to assign subscriber to MailerLite group.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setFeedbackMessage('');

    if (!MAILERLITE_TOKEN) {
      setStatus('error');
      setFeedbackMessage(
        'Newsletter signups are not configured yet. Please add REACT_APP_MAILERLITE_TOKEN to your environment.'
      );
      return;
    }

    const payload = {
      email: formData.email.trim(),
    };

    if (formData.name.trim()) {
      payload.fields = { name: formData.name.trim() };
    }

    if (MAILERLITE_GROUP_ID) {
      payload.groups = [MAILERLITE_GROUP_ID];
    }

    try {
      const response = await fetch(MAILERLITE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${MAILERLITE_TOKEN}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 409) {
        try {
          await ensureSubscriberInGroup(formData.email.trim());
        } catch (groupError) {
          console.error('Failed to update group membership', groupError);
        }
        setStatus('success');
        setFeedbackMessage('You are already subscribed. Thank you for your support!');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const apiErrorMessage =
          errorData?.message ||
          (Array.isArray(errorData?.errors)
            ? errorData.errors.map((err) => err?.message).filter(Boolean).join(' ')
            : null);
        throw new Error(apiErrorMessage || 'Unable to subscribe right now. Please try again in a moment.');
      }

      setStatus('success');
      setFeedbackMessage('Thanks for subscribing! Please check your email for a welcome message.');
      setFormData({ name: '', email: '' });
    } catch (error) {
      console.error('Failed to subscribe to newsletter', error);
      setStatus('error');
      setFeedbackMessage(error.message);
    }
  };

  return (
    <div className="NewsletterPage">
      <header className="Newsletter-header">
        <Link to="/" className="logo-link">
          <img src={logo} className="Newsletter-logo" alt="logo" />
        </Link>

        <div className="form-container">
          <form className="newsletter-form" onSubmit={handleSubmit}>
            <label htmlFor="newsletter-name">Name (optional)</label>
            <input
              id="newsletter-name"
              name="name"
              type="text"
              onChange={handleChange}
              value={formData.name}
              autoComplete="name"
            />

            <label htmlFor="newsletter-email">Email *</label>
            <input
              id="newsletter-email"
              name="email"
              type="email"
              onChange={handleChange}
              value={formData.email}
              autoComplete="email"
              required
            />

            <button type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Submitting...' : 'Sign up'}
            </button>
          </form>

          {feedbackMessage && (
            <p
              className={`form-feedback ${status === 'success' ? 'success' : 'error'}`}
              role={status === 'success' ? 'status' : 'alert'}
            >
              {feedbackMessage}
            </p>
          )}

          {!MAILERLITE_TOKEN && process.env.NODE_ENV !== 'production' && (
            <p className="form-feedback error" role="alert">
              Add REACT_APP_MAILERLITE_TOKEN to your .env file to enable signups.
            </p>
          )}
        </div>

        <div className="social-links">
          <a href="mailto:contact@example.com" className="mail-link">
            <img src={mailIcon} alt="Mail Icon" className="mail-icon" />
          </a>
          <a
            href="https://www.instagram.com/outer_spaces_sessions/"
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-link"
          >
            <img
              src={instagramIcon}
              alt="Instagram Icon"
              className="instagram-icon"
            />
          </a>
        </div>

        <Link to="/" className="back-link">
          ← Back to Sessions
        </Link>
      </header>
    </div>
  );
}

export default NewsletterPage;
